import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, query, where, orderBy, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Card, Select, Button, Alert, Tooltip, Space, Typography, Tag, List, Divider, Modal } from 'antd';
import { 
  NodeIndexOutlined, 
  WarningOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  UnlinkOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { Text, Title } = Typography;

/**
 * Task Dependency Mapper - Phase 2 Implementation
 * 
 * Features:
 * 1. Visual dependency mapping
 * 2. Circular dependency detection
 * 3. Logical dependency validation
 * 4. Interactive dependency editor
 */
const TaskDependencyMapper = ({ incidentId, incidentData }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [tasks, setTasks] = useState([]);
  const [dependencies, setDependencies] = useState(new Map());
  const [circularDependencies, setCircularDependencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAddDependency, setShowAddDependency] = useState(false);
  const [userOrgId, setUserOrgId] = useState(null);
  const canvasRef = useRef(null);

  // Get user's orgId
  useEffect(() => {
    const fetchUserOrgId = async () => {
      if (!currentUser?.uid || !db) return;
      
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserOrgId(userDoc.data().orgId);
        }
      } catch (error) {
        console.error('Error fetching user orgId:', error);
      }
    };
    
    fetchUserOrgId();
  }, [currentUser?.uid, db]);

  // Load tasks and analyze dependencies
  useEffect(() => {
    if (incidentId && userOrgId) {
      loadTasksAndDependencies();
    }
  }, [incidentId, userOrgId]);

  const loadTasksAndDependencies = async () => {
    try {
      setLoading(true);
      
      const tasksRef = collection(db, 'c3_tasks');
      const q = query(
        tasksRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', userOrgId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const taskList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setTasks(taskList);
      
      // Build dependency map
      const depMap = new Map();
      taskList.forEach(task => {
        if (task.prerequisites && task.prerequisites.length > 0) {
          depMap.set(task.id, task.prerequisites);
        }
      });
      setDependencies(depMap);
      
      // Detect circular dependencies
      const circular = detectCircularDependencies(taskList, depMap);
      setCircularDependencies(circular);

      console.log('🔗 Dependency analysis:', {
        totalTasks: taskList.length,
        tasksWithDependencies: depMap.size,
        circularDependencies: circular.length
      });

    } catch (error) {
      console.error('Error loading tasks and dependencies:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * CIRCULAR DEPENDENCY DETECTION ALGORITHM
   */
  const detectCircularDependencies = (taskList, depMap) => {
    const visited = new Set();
    const recursionStack = new Set();
    const circularPaths = [];

    const dfs = (taskId, path = []) => {
      if (recursionStack.has(taskId)) {
        // Found a cycle - extract the circular part
        const cycleStart = path.indexOf(taskId);
        const cyclePath = path.slice(cycleStart).concat([taskId]);
        circularPaths.push(cyclePath);
        return true;
      }

      if (visited.has(taskId)) {
        return false;
      }

      visited.add(taskId);
      recursionStack.add(taskId);
      
      const prerequisites = depMap.get(taskId) || [];
      for (const prereqId of prerequisites) {
        if (dfs(prereqId, [...path, taskId])) {
          return true;
        }
      }

      recursionStack.delete(taskId);
      return false;
    };

    // Check each task
    taskList.forEach(task => {
      if (!visited.has(task.id)) {
        dfs(task.id);
      }
    });

    return circularPaths.map(path => ({
      path,
      tasks: path.map(id => taskList.find(t => t.id === id)).filter(Boolean),
      severity: path.length > 3 ? 'high' : 'medium'
    }));
  };

  /**
   * DEPENDENCY VALIDATION
   */
  const validateDependencies = () => {
    const issues = [];
    
    dependencies.forEach((prerequisites, taskId) => {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      // Check for invalid prerequisites
      const invalidPrereqs = prerequisites.filter(prereqId => 
        !tasks.find(t => t.id === prereqId)
      );

      if (invalidPrereqs.length > 0) {
        issues.push({
          type: 'invalid_prerequisite',
          taskId,
          taskName: task.name,
          issue: `References non-existent tasks: ${invalidPrereqs.join(', ')}`,
          severity: 'high'
        });
      }

      // Check for self-dependencies
      if (prerequisites.includes(taskId)) {
        issues.push({
          type: 'self_dependency',
          taskId,
          taskName: task.name,
          issue: 'Task depends on itself',
          severity: 'critical'
        });
      }

      // Check for logical conflicts (completed task depending on incomplete)
      if (task.status === 'complete') {
        const incompletePrereqs = prerequisites
          .map(id => tasks.find(t => t.id === id))
          .filter(prereq => prereq && prereq.status !== 'complete');

        if (incompletePrereqs.length > 0) {
          issues.push({
            type: 'logical_conflict',
            taskId,
            taskName: task.name,
            issue: `Completed task depends on incomplete tasks: ${incompletePrereqs.map(t => t.name).join(', ')}`,
            severity: 'medium'
          });
        }
      }
    });

    return issues;
  };

  /**
   * DEPENDENCY VISUALIZATION
   */
  const getDependencyLevels = () => {
    const levels = new Map();
    const visited = new Set();

    const calculateLevel = (taskId, level = 0) => {
      if (visited.has(taskId)) {
        return levels.get(taskId) || 0;
      }

      visited.add(taskId);
      const prerequisites = dependencies.get(taskId) || [];
      
      if (prerequisites.length === 0) {
        levels.set(taskId, level);
        return level;
      }

      const maxPrereqLevel = Math.max(
        ...prerequisites.map(prereqId => calculateLevel(prereqId, level + 1))
      );

      const taskLevel = maxPrereqLevel + 1;
      levels.set(taskId, taskLevel);
      return taskLevel;
    };

    tasks.forEach(task => {
      if (!visited.has(task.id)) {
        calculateLevel(task.id);
      }
    });

    return levels;
  };

  /**
   * ADD/REMOVE DEPENDENCIES
   */
  const addDependency = async (taskId, prerequisiteId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const currentPrereqs = task.prerequisites || [];
      if (currentPrereqs.includes(prerequisiteId)) {
        return; // Already exists
      }

      // Check for circular dependency before adding
      const tempDeps = new Map(dependencies);
      tempDeps.set(taskId, [...currentPrereqs, prerequisiteId]);
      const wouldCauseCircular = detectCircularDependencies(tasks, tempDeps);

      if (wouldCauseCircular.length > 0) {
        Modal.error({
          title: 'Circular Dependency Detected',
          content: 'Adding this dependency would create a circular dependency. Please choose a different prerequisite.',
        });
        return;
      }

      const newPrereqs = [...currentPrereqs, prerequisiteId];
      const taskRef = doc(db, 'c3_tasks', taskId);
      
      await updateDoc(taskRef, {
        prerequisites: newPrereqs,
        updatedAt: new Date()
      });

      // Refresh data
      await loadTasksAndDependencies();

      console.log(`✅ Added dependency: ${prerequisiteId} -> ${taskId}`);

    } catch (error) {
      console.error('Error adding dependency:', error);
    }
  };

  const removeDependency = async (taskId, prerequisiteId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const currentPrereqs = task.prerequisites || [];
      const newPrereqs = currentPrereqs.filter(id => id !== prerequisiteId);
      
      const taskRef = doc(db, 'c3_tasks', taskId);
      
      await updateDoc(taskRef, {
        prerequisites: newPrereqs,
        updatedAt: new Date()
      });

      // Refresh data
      await loadTasksAndDependencies();

      console.log(`🗑️ Removed dependency: ${prerequisiteId} -> ${taskId}`);

    } catch (error) {
      console.error('Error removing dependency:', error);
    }
  };

  /**
   * RENDER HELPERS
   */
  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'complete': return '#52c41a';
      case 'in_progress': return '#1890ff';
      case 'blocked': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'recommended': return 'orange';
      default: return 'blue';
    }
  };

  const dependencyLevels = getDependencyLevels();
  const validationIssues = validateDependencies();
  const tasksWithDependencies = tasks.filter(t => dependencies.has(t.id));
  const independentTasks = tasks.filter(t => !dependencies.has(t.id));

  if (loading) {
    return (
      <Card title="🔗 Task Dependency Mapping" loading={true}>
        Analyzing task dependencies...
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <NodeIndexOutlined />
          <span>Task Dependency Mapping</span>
          <Tag color="blue">{tasks.length} tasks</Tag>
          <Tag color="orange">{tasksWithDependencies.length} with dependencies</Tag>
          {circularDependencies.length > 0 && (
            <Tag color="red">{circularDependencies.length} circular</Tag>
          )}
        </Space>
      }
      style={{ marginBottom: '16px' }}
    >
      {/* VALIDATION ALERTS */}
      {circularDependencies.length > 0 && (
        <Alert
          type="error"
          message="Circular Dependencies Detected"
          description={
            <div>
              <Text>The following circular dependencies were found and must be resolved:</Text>
              {circularDependencies.map((circular, index) => (
                <div key={index} style={{ marginTop: '8px' }}>
                  <Text strong>Circular Path {index + 1}:</Text>
                  <div style={{ marginLeft: '16px' }}>
                    {circular.tasks.map((task, i) => (
                      <span key={task.id}>
                        <Tag color={getPriorityColor(task.priority)}>{task.name}</Tag>
                        {i < circular.tasks.length - 1 && ' → '}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          }
          style={{ marginBottom: '16px' }}
          showIcon
        />
      )}

      {validationIssues.length > 0 && (
        <Alert
          type="warning"
          message={`${validationIssues.length} Dependency Issues Found`}
          description={
            <List
              size="small"
              dataSource={validationIssues}
              renderItem={issue => (
                <List.Item>
                  <Space>
                    <Tag color={issue.severity === 'critical' ? 'red' : issue.severity === 'high' ? 'orange' : 'blue'}>
                      {issue.severity}
                    </Tag>
                    <Text strong>{issue.taskName}:</Text>
                    <Text>{issue.issue}</Text>
                  </Space>
                </List.Item>
              )}
            />
          }
          style={{ marginBottom: '16px' }}
          showIcon
        />
      )}

      {/* DEPENDENCY HIERARCHY VISUALIZATION */}
      <div style={{ marginBottom: '16px' }}>
        <Title level={5}>Dependency Hierarchy</Title>
        
        {Array.from(new Set(dependencyLevels.values())).sort((a, b) => b - a).map(level => {
          const levelTasks = tasks.filter(task => dependencyLevels.get(task.id) === level);
          if (levelTasks.length === 0) return null;

          return (
            <div key={level} style={{ marginBottom: '12px' }}>
              <Text strong>Level {level}: </Text>
              <Space wrap>
                {levelTasks.map(task => (
                  <Tooltip key={task.id} title={`${task.name} - ${task.status}`}>
                    <Tag 
                      color={getPriorityColor(task.priority)}
                      style={{ 
                        borderLeft: `4px solid ${getTaskStatusColor(task.status)}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedTask(task)}
                    >
                      {task.name.substring(0, 20)}
                      {task.name.length > 20 && '...'}
                    </Tag>
                  </Tooltip>
                ))}
              </Space>
            </div>
          );
        })}

        {independentTasks.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <Text strong>Independent Tasks: </Text>
            <Space wrap>
              {independentTasks.map(task => (
                <Tooltip key={task.id} title={`${task.name} - ${task.status} (No dependencies)`}>
                  <Tag 
                    color="green"
                    style={{ 
                      borderLeft: `4px solid ${getTaskStatusColor(task.status)}`,
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedTask(task)}
                  >
                    {task.name.substring(0, 20)}
                    {task.name.length > 20 && '...'}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </div>
        )}
      </div>

      {/* TASK DETAIL PANEL */}
      {selectedTask && (
        <Card 
          size="small" 
          title={`Dependencies for: ${selectedTask.name}`}
          extra={
            <Button 
              size="small" 
              onClick={() => setSelectedTask(null)}
            >
              Close
            </Button>
          }
          style={{ marginBottom: '16px' }}
        >
          <div style={{ marginBottom: '12px' }}>
            <Space>
              <Tag color={getPriorityColor(selectedTask.priority)}>
                {selectedTask.priority || 'normal'}
              </Tag>
              <Tag color={getTaskStatusColor(selectedTask.status)}>
                {selectedTask.status}
              </Tag>
              {selectedTask.owner && <Tag>Owner: {selectedTask.owner}</Tag>}
            </Space>
          </div>

          {/* Current Prerequisites */}
          <div style={{ marginBottom: '12px' }}>
            <Text strong>Prerequisites:</Text>
            {(selectedTask.prerequisites || []).length === 0 ? (
              <Text type="secondary"> None</Text>
            ) : (
              <div style={{ marginTop: '4px' }}>
                {(selectedTask.prerequisites || []).map(prereqId => {
                  const prereqTask = tasks.find(t => t.id === prereqId);
                  return prereqTask ? (
                    <Tag 
                      key={prereqId}
                      closable
                      onClose={() => removeDependency(selectedTask.id, prereqId)}
                      color={getTaskStatusColor(prereqTask.status)}
                    >
                      {prereqTask.name}
                    </Tag>
                  ) : (
                    <Tag key={prereqId} color="red">Unknown Task</Tag>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Dependency */}
          <div>
            <Space>
              <Text strong>Add Prerequisite:</Text>
              <Select
                style={{ width: 200 }}
                placeholder="Select prerequisite task"
                value={null}
                onChange={(prereqId) => addDependency(selectedTask.id, prereqId)}
              >
                {tasks
                  .filter(t => 
                    t.id !== selectedTask.id && 
                    !(selectedTask.prerequisites || []).includes(t.id)
                  )
                  .map(task => (
                    <Option key={task.id} value={task.id}>
                      {task.name} ({task.status})
                    </Option>
                  ))}
              </Select>
            </Space>
          </div>

          {/* Dependent Tasks */}
          <Divider />
          <div>
            <Text strong>Tasks that depend on this:</Text>
            {(() => {
              const dependentTasks = tasks.filter(t => 
                (t.prerequisites || []).includes(selectedTask.id)
              );
              return dependentTasks.length === 0 ? (
                <Text type="secondary"> None</Text>
              ) : (
                <div style={{ marginTop: '4px' }}>
                  {dependentTasks.map(depTask => (
                    <Tag 
                      key={depTask.id}
                      color={getTaskStatusColor(depTask.status)}
                      onClick={() => setSelectedTask(depTask)}
                      style={{ cursor: 'pointer' }}
                    >
                      {depTask.name}
                    </Tag>
                  ))}
                </div>
              );
            })()}
          </div>
        </Card>
      )}

      {/* SUMMARY STATS */}
      <Card size="small" title="Dependency Statistics">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <Text>{independentTasks.length} independent tasks (can start immediately)</Text>
          </Space>
          <Space>
            <LinkOutlined style={{ color: '#1890ff' }} />
            <Text>{tasksWithDependencies.length} tasks with dependencies</Text>
          </Space>
          <Space>
            <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
            <Text>{validationIssues.length} validation issues</Text>
          </Space>
          {circularDependencies.length > 0 && (
            <Space>
              <WarningOutlined style={{ color: '#ff4d4f' }} />
              <Text>{circularDependencies.length} circular dependencies (critical)</Text>
            </Space>
          )}
          <Space>
            <NodeIndexOutlined style={{ color: '#722ed1' }} />
            <Text>Maximum dependency depth: {Math.max(...dependencyLevels.values(), 0)}</Text>
          </Space>
        </Space>
      </Card>
    </Card>
  );
};

export default TaskDependencyMapper; 